import React from 'react';
import { ColumnContainer } from 'utils/columnsStyle';
import { Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectNoteToMigrate,
  cancelMigration,
  resendMigration,
  toggleFivePerThousand,
} from 'redux/actions/notesMigration/notesMigrationActions';
import { green, red, blue, orange } from '@mui/material/colors';
import { getDecimalFormat } from 'helpers/getNumberFormats';
import { ModalV2 } from 'components/Modal/ModalV2';
import { useToggle } from "hooks/useToggle";
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import RegularButton from "components/CustomButtons/Button";
import { PERMISSIONS } from "router/permissions";
import { useRuleValidator } from "hooks/useRuleValidator";

const cVal = 500;

const STATUS_DISPLAY = {
  ERROR: { label: 'ERROR', color: red[cVal], fontWeight: 800 },
  ERROR_ENVIO: { label: 'ERROR DE ENVÍO', color: red[cVal], fontWeight: 800 },
  CANCELADA: { label: 'MIGRACIÓN CANCELADA', color: orange[800], fontWeight: 800 },
};
const getStatusDisplay = (status) => STATUS_DISPLAY[status] || { label: status };
// Status en los que la nota sigue SIN_ENVIAR en DB y se puede seleccionar para migrar
const MIGRATABLE_STATUSES = ['SIN_ENVIAR', 'ERROR_ENVIO'];
const normalize = (str) => (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const ActionsCell = ({ original }) => {
  const dispatch = useDispatch();
  const { id, statusMigration, checked, folio } = original;
  const { isLoadingComp } = useSelector(({ search }) => search);
  const [open, setOpen] = useToggle();
  const [openResend, setOpenResend] = useToggle();
  const hasFivePerThousandRule = useRuleValidator(PERMISSIONS.INVOICES_ERP_MIGRATION_5ALMILLAR);
  const hasCancelMigrationRule = useRuleValidator(PERMISSIONS.INVOICES_ERP_MIGRATION_CANCEL);

  const handleIsMigration = () => {
    dispatch(selectNoteToMigrate(id));
  };
  const handleCancelMigration = () => {
    dispatch(cancelMigration({ ids: [id] }, setOpen));
  };
  const handleResendMigration = () => {
    dispatch(resendMigration({ ids: [id] }, setOpenResend));
  };

  return (
    <>
      <ColumnContainer>
        {
          MIGRATABLE_STATUSES.includes(statusMigration)
          &&
          <Tooltip title="Migrar" placement="top">
            <FormControlLabel
              control={<Checkbox
                color='success'
                checked={checked}
                onChange={handleIsMigration}
                name='migrate'
                size='small'
                sx={{
                  color: red[cVal],
                  '&.Mui-checked': {
                    color: green[cVal],
                },
              }}
            />}
          />
          </Tooltip>
        }
        {
          hasFivePerThousandRule && MIGRATABLE_STATUSES.includes(statusMigration) &&
          <Tooltip title="5 al millar" placement="top">
            <FormControlLabel
              control={<Checkbox
                color="info"
                checked={original.isFivePerThousand || false}
                onChange={() => dispatch(toggleFivePerThousand(id))}
                name='fivePerThousand'
                size='small'
                sx={{
                  color: blue[300],
                  '&.Mui-checked': {
                    color: blue[600],
                  },
                }}
              />}
            />
          </Tooltip>
        }
        {
          hasCancelMigrationRule && statusMigration === "ORACLE" &&
          <FormControlLabel
            control={
              <RegularButton
                color="transparent"
                size="sm"
                justIcon
                round
                onClick={setOpen}
                tooltip="Cancelar migración en Oracle"
              >
                <CloudOffIcon style={{ color: red[500] }} />
              </RegularButton>
            } />
        }
        {
          hasCancelMigrationRule && (statusMigration === "ERROR" || statusMigration === "CANCELADA") &&
          <FormControlLabel
            control={
              <RegularButton
                color="transparent"
                size="sm"
                justIcon
                round
                onClick={setOpenResend}
                tooltip="Reenviar a migrar"
              >
                <CloudSyncIcon style={{ color: green[cVal] }} />
              </RegularButton>
            } />
        }
      </ColumnContainer>
      <ModalV2
        open={open}
        onClose={setOpen}
        title={`¿Cancelar nota con folio ${folio}?`}
        justConfirm
        onSubmit={handleCancelMigration}
        isLoading={isLoadingComp?.loadingNotesMigrationActions}
      />
      <ModalV2
        open={openResend}
        onClose={setOpenResend}
        title={`¿Reenviar nota con folio ${folio}?`}
        justConfirm
        onSubmit={handleResendMigration}
        isLoading={isLoadingComp?.loadingNotesMigrationActions}
      />
    </>
  );
};

export const columns = [
  {
    Header: 'Folio',
    accessor: 'folio',
    filterable: true,
    filterMethod: (filter, row) => row.folio?.toString().includes(filter.value.toLowerCase())
  },
  {
    Header: 'Fecha Nota',
    accessor: 'date',
    filterable: true,
    filterMethod: (filter, row) => row.date?.toLowerCase().includes(filter.value.toLowerCase())
  },
  {
    Header: 'No. Cliente ERP',
    accessor: 'oracleCustomerNumber',
    filterable: true,
    filterMethod: (filter, row) => row.oracleCustomerNumber?.toString().includes(filter.value.toLowerCase())
  },
  {
    Header: 'Cliente',
    filterable: true,
    accessor: 'customer',
    filterMethod: (filter, row) => row.customer?.toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => {
      const { customer, customerSlice } = original
      return <Tooltip
        title={customer}
      ><div>{customerSlice} </div></Tooltip>
    }
  },
  {
    Header: 'Cuenta',
    accessor: 'account',
    filterable: true,
    filterMethod: (filter, row) => row.account?.toLowerCase().includes(filter.value.toLowerCase())
  },
  {
    Header: 'Codigo',
    accessor: 'accountCode',
    filterable: true,
    filterMethod: (filter, row) => row.accountCode?.toString().includes(filter.value.toLowerCase())
  },
  {
    Header: 'Unidad',
    accessor: 'unitNumber',
    filterable: true,
    filterMethod: (filter, row) => row.unitNumber?.toLowerCase().includes(filter.value.toLowerCase())
  },
  {
    Header: 'Fecha liquidación',
    accessor: 'settlementDate',
    filterable: true,
    filterMethod: (filter, row) => row.settlementDate?.toLowerCase().includes(filter.value.toLowerCase())
  },
  {
    Header: 'Tipo',
    accessor: 'type',
    filterable: true,
    filterMethod: (filter, row) => row.type?.toLowerCase().includes(filter.value.toLowerCase())
  },
  {
    Header: 'Total',
    accessor: 'total',
    filterable: true,
    filterMethod: (filter, row) => row.total?.toString().toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => {
      const { total } = original
      return <div>{getDecimalFormat(total)} </div>
    }
  },
  {
    Header: 'Timbrado',
    accessor: 'idInvoice',
    filterable: true,
    filterMethod: (filter, row) => row.idInvoice?.toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: 'Estatus',
    accessor: 'statusMigration',
    filterable: true,
    filterMethod: (filter, row) =>
      normalize(getStatusDisplay(row.statusMigration).label).includes(normalize(filter.value)),
    Cell: ({ original }) => {
      const { label, color, fontWeight } = getStatusDisplay(original.statusMigration)
      return <span style={{ color, fontWeight }}>{label}</span>
    }
  },
  {
    Header: 'CRE',
    accessor: 'creLicense',
    filterable: true,
    filterMethod: (filter, row) => row.creLicense?.toLowerCase().includes(filter.value.toLowerCase())
  },
  {
    Header: 'Acciones',
    filterable: true,
    sortable: true,
    Cell: ({ original }) => <ActionsCell original={original} />,
  }
];